
import React from 'react';
import { Label } from '../components-shadcn/label';
import { ReactComponent as CardIcon } from '../assets/card_blue.svg'
import { ReactComponent as NameIcon } from '../assets/name.svg'
import { ReactComponent as CalendarIcon } from '../assets/calendar.svg'
import { ReactComponent as EmailIcon } from '../assets/email.svg'
import { ReactComponent as CellPhoneIcon } from '../assets/cellphone.svg'
import { ReactComponent as CPFIcon } from '../assets/cpf_icon.svg'
import { isBlankValue, isValidCEP, isValidCNPJ, isValidCPF, isValidEmail, isValidPhone, maskCnpj, sanitizeCnpjInput, unmaskCnpj } from '../lib/utils';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../components-shadcn/input-group';

type IconKey = 'card' | 'name' | 'calendar' | 'email' | 'cellphone' | 'none' | 'cpf'
type TypeInput = 'card_number' | 'card_name' | 'card_data' | 'card_cvv' | 'card_cpf' | 'email' | 'cellphone' | 'cnpj' | 'generic' | 'cep'

interface Props {
  icon: IconKey;
  title: string;
  placeholder: string;
  type: TypeInput;
  value: string;
  error: string;
  onChange: (value: string) => void;
  onError: (value: string) => void;
  required?: boolean
  ref?: React.Ref<HTMLInputElement>;
}

interface Args {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  type: string;
  inputMode: "none" | "search" | "text" | "numeric" | "tel" | "url" | "email" | "decimal" | undefined;
}

const CustomInput: React.FC<Props> = ({ icon, title, placeholder, type, value, onChange, onError, error, required, ref }) => {

  type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>

  const icon_model: Record<IconKey, IconComponent> = {
    'card': CardIcon,
    'name': NameIcon,
    'calendar': CalendarIcon,
    'email': EmailIcon,
    'cellphone': CellPhoneIcon,
    'none': CellPhoneIcon,
    'cpf': CPFIcon
  }
  const IconComponent = icon_model[icon];

  // Regra de negócio de formatação do input de numero de cartão de credito
  const handleChangeInputNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove tudo que não for número
    let input = e.target.value.replace(/\D/g, "");

    // Detecta American Express (começa com 34 ou 37)
    const isAmex = /^3[47]/.test(input);

    // Define o limite de dígitos conforme o tipo
    const maxLength = isAmex ? 15 : 16;
    if (input.length > maxLength) {
      input = input.slice(0, maxLength);
    }

    // Aplica a formatação
    let formatted;
    if (isAmex) {
      // Amex → 4 + 6 + 5
      formatted = input
        .replace(/^(\d{4})(\d{0,6})(\d{0,5}).*/, (_, g1, g2, g3) =>
          [g1, g2, g3].filter(Boolean).join(" ")
        );
    } else {
      // Demais cartões → grupos de 4
      formatted = input.replace(/(\d{4})(?=\d)/g, "$1 ");
    }

    onChange(formatted);
  };

  // Regra de negócio de validação de input de numero de cartão de credito
  const handleBlurInputNumber = () => {
    const onlyNumbers = value.replace(/\s/g, "");
    const isAmex = /^3[47]/.test(onlyNumbers);

    const validLength = isAmex ? 15 : 16;

    // 1. Verificar tamanho
    if (onlyNumbers.length > 0 && onlyNumbers.length !== validLength) {
      onError("Número inválido");
      return;
    }

    // 2. Algoritmo de Luhn
    const digits = onlyNumbers.split("").reverse().map(Number);

    const sum = digits.reduce((acc, digit, idx) => {
      if (idx % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      return acc + digit;
    }, 0);

    const isValid = sum % 10 === 0;

    if (!isValid) {
      onError("Número inválido");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação do input de nome
  const handleChangeInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };
  // Regra de negócio de validação de input de nome
  const handleBlurInputName = () => {
    const parts = value.trim().split(/\s+/); // separa por espaço(s)
    if (parts.length < 2) {
      onError("Informe pelo menos nome e sobrenome.");
    } else {
      onError("");
    }
  };
  // Regra de megócio de formatação do input de data
  const handleChangeInputData = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // só números

    // Limita a 4 dígitos (MMYY)
    if (input.length > 4) {
      input = input.slice(0, 4);
    }

    // Formata para MM/YY
    if (input.length >= 3) {
      input = input.replace(/(\d{2})(\d{1,2})/, "$1/$2");
    }

    onChange(input);
  };
  // Regra de negócio de validação de input de data
  const handleBlurInputData = () => {
    // Validação
    const match = value.match(/^(\d{2})\/(\d{2})$/);
    if (!match) {
      onError("A data deve estar no formato MM/AA.");
      return;
    }

    const month = parseInt(match[1], 10);
    if (month < 1 || month > 12) {
      onError("Mês inválido.");
      return;
    }

    onError("");
  };
  // Regra de negócio de formatação de input de CVV
  const handleChangeInputCVV = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // só números

    // Limita a 3 dígitos
    if (input.length > 3) {
      input = input.slice(0, 3);
    }

    onChange(input);
  };
  // Regra de negócio de validação de input de CVV
  const handleBlurInputCVV = () => {
    if (value.length > 0 && value.length < 3) {
      onError("O CVV deve ter 3 dígitos.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação de input de CPF
  const handleChangeInputCPF = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // só números

    // Limita a 11 dígitos
    if (input.length > 11) {
      input = input.slice(0, 11);
    }

    // Aplica a máscara: 000.000.000-00
    if (input.length > 9) {
      input = input.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, "$1.$2.$3-$4");
    } else if (input.length > 6) {
      input = input.replace(/(\d{3})(\d{3})(\d{0,3})/, "$1.$2.$3");
    } else if (input.length > 3) {
      input = input.replace(/(\d{3})(\d{0,3})/, "$1.$2");
    }

    onChange(input);
  };
  // Regra de negócio de validação de input de CPF
  const handleBlurInputCPF = () => {
    const digits = value.replace(/\D/g, ""); // remove pontos e traço
    if (digits.length > 0 && digits.length < 11) {
      onError("O CPF deve ter 11 dígitos.");
    } else if (digits.length === 11 && !isValidCPF(digits)) {
      onError("CPF inválido.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação de input de Email
  const handleChangeInputEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove espaços e força minúsculas
    input = input.replace(/\s/g, "").toLowerCase();

    onChange(input);
  };
  // Regra de negócio de validação de input de Email
  const handleBlurInputEmail = () => {
    if (value.length > 0 && !isValidEmail(value)) {
      onError("E-mail inválido.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação de input de Telefone
  const handleChangeInputPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // só números

    // Limita a 11 dígitos (DDD + 9 dígitos do celular)
    if (input.length > 11) {
      input = input.slice(0, 11);
    }

    // Aplica a máscara: (00) 0 0000-0000
    if (input.length > 10) {
      input = input.replace(/(\d{2})(\d{1})(\d{4})(\d{0,4})/, "($1) $2 $3-$4");
    } else if (input.length > 6) {
      input = input.replace(/(\d{2})(\d{1})(\d{0,4})/, "($1) $2 $3");
    } else if (input.length > 2) {
      input = input.replace(/(\d{2})(\d{0,5})/, "($1) $2");
    } else {
      input = input.replace(/(\d*)/, "($1");
    }

    onChange(input);
  };
  // Regra de negócio de validação de input de Telefone
  const handleBlurInputPhone = () => {
    const digits = value.replace(/\D/g, "");
    if (digits.length > 0 && digits.length < 11) {
      onError("O telefone deve ter 11 dígitos (DDD + número).");
    } else if (digits.length === 11 && !isValidPhone(value)) {
      onError("Telefone inválido.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação de input de CNPJ (alfanumérico, IN RFB 2.229/2024):
  // 12 primeiras posições aceitam A-Z e 0-9; os 2 dígitos verificadores continuam só numéricos.
  const handleChangeInputCNPJ = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(maskCnpj(sanitizeCnpjInput(e.target.value)));
  };
  // Regra de negócio de validação de input de CNPJ
  const handleBlurInputCNPJ = () => {
    const raw = unmaskCnpj(value);
    if (raw.length > 0 && raw.length < 14) {
      onError("O CNPJ deve ter 14 dígitos.");
    } else if (raw.length === 14 && !isValidCNPJ(value)) {
      onError("CNPJ inválido.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de formatação de input Genérico
  const handleChangeInputGeneric = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value; // sem máscara, valor cru
    onChange(input);
  };
  // Regra de negócio de validação de input em branco
  const handleBlurInputGeneric = () => {
    onError("");
  };
  // Regra de negócio de formatação de input de CEP
  const handleChangeInputCEP = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // só números

    // Limita a 8 dígitos
    if (input.length > 8) {
      input = input.slice(0, 8);
    }

    // Aplica a máscara: 00000-000
    if (input.length > 5) {
      input = input.replace(/(\d{5})(\d{0,3})/, "$1-$2");
    }

    onChange(input);
  };
  // Regra de negócio de validação de input de CEP
  const handleBlurInputCEP = () => {
    const digits = value.replace(/\D/g, "");

    if (digits.length > 0 && digits.length < 8) {
      onError("O CEP deve ter 8 dígitos.");
    } else if (digits.length === 8 && !isValidCEP(value)) {
      onError("CEP inválido.");
    } else {
      onError("");
    }
  };
  // Regra de negócio de validação de input obrigatorio
  const handleBlurInputRequired = () => {
    if (!isBlankValue(value)) {
      onError("Campo obrigatório.");
    }
  };

  const type_model: Record<TypeInput, Args> = {
    card_number: {
      handleChange: handleChangeInputNumber,
      handleBlur: handleBlurInputNumber,
      type: 'text',
      inputMode: 'numeric'
    },
    card_name: {
      handleChange: handleChangeInputName,
      handleBlur: handleBlurInputName,
      type: 'text',
      inputMode: 'text'
    },
    card_data: {
      handleChange: handleChangeInputData,
      handleBlur: handleBlurInputData,
      type: 'text',
      inputMode: 'numeric'
    },
    card_cvv: {
      handleChange: handleChangeInputCVV,
      handleBlur: handleBlurInputCVV,
      type: 'text',
      inputMode: 'numeric'
    },
    card_cpf: {
      handleChange: handleChangeInputCPF,
      handleBlur: handleBlurInputCPF,
      type: 'text',
      inputMode: 'numeric'
    },
    email: {
      handleChange: handleChangeInputEmail,
      handleBlur: handleBlurInputEmail,
      type: 'text',
      inputMode: 'email'
    },
    cellphone: {
      handleChange: handleChangeInputPhone,
      handleBlur: handleBlurInputPhone,
      type: 'text',
      inputMode: 'tel'
    },
    cnpj: {
      handleChange: handleChangeInputCNPJ,
      handleBlur: handleBlurInputCNPJ,
      type: 'text',
      inputMode: 'text'
    },
    cep: {
      handleChange: handleChangeInputCEP,
      handleBlur: handleBlurInputCEP,
      type: 'text',
      inputMode: 'text'
    },
    generic: {
      handleChange: handleChangeInputGeneric,
      handleBlur: handleBlurInputGeneric,
      type: 'text',
      inputMode: 'text'
    },
  }

  type_model[type].handleChange({
    target: { value } as unknown as EventTarget & HTMLInputElement,
  } as React.ChangeEvent<HTMLInputElement>);

  return (
    <div className="w-full text-blue-primary">
      <Label htmlFor="email" className='text-[0.8rem] font-[600] text-blue-primary'>{title}{required === true && <span className="text-red-primary">*</span>}</Label>
      <InputGroup className='border-0 border-b-[1px] !p-[0rem] border-gray-300 rounded-none'>
        <InputGroupAddon>
          {icon !== "none" && <IconComponent />}
        </InputGroupAddon>
        <InputGroupInput
          placeholder={placeholder}
          type={type_model[type].type}
          value={value}
          onChange={type_model[type].handleChange}
          inputMode={type_model[type].inputMode}
          autoCapitalize={type === 'cnpj' ? 'characters' : undefined}
          onBlur={() => { type_model[type].handleBlur(); required === true && handleBlurInputRequired(); }}
          ref={ref}
        />
      </InputGroup>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}

export default CustomInput;
